import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, ChevronLeft, ChevronRight } from "@/components/Icon";
import { useEffect, useState } from "react";
import { useToptexProducts, imageSrcSet, imageFallback, imageSrc, type NormProduct } from "@/hooks/useToptex";
import imgTshirts from "@/assets/categories/card-t-shirts.webp";
import imgPolos from "@/assets/categories/card-polos.webp";
import imgSweaters from "@/assets/categories/card-sweaters-en-hoodies.webp";
import imgJassen from "@/assets/categories/card-jassen-en-bodywarmers.webp";
import imgWerk from "@/assets/categories/card-werkkleding.webp";
import imgSport from "@/assets/categories/card-sportkleding.webp";
import imgCaps from "@/assets/categories/card-caps-en-mutsen.webp";
import imgAccessoires from "@/assets/categories/card-accessoires.webp";
import imgTassen from "@/assets/categories/card-tassen.webp";
import imgKids from "@/assets/categories/card-kids-en-baby.webp";


const PAGE_SIZE_OPTIONS = [12, 24, 36] as const;
const DEFAULT_PAGE_SIZE = 12;

const CATEGORIES: { label: string; key: string; description: string; image: string }[] = [
  { label: "T-shirts", key: "tshirt", description: "Klassieke en moderne t-shirts in alle kleuren", image: imgTshirts },
  { label: "Polo's", key: "polo", description: "Zakelijke en sportieve polo's", image: imgPolos },
  { label: "Sweaters & Hoodies", key: "sweater", description: "Warme sweaters en hoodies", image: imgSweaters },
  { label: "Jassen & Bodywarmers", key: "jacket", description: "Jassen, softshells en bodywarmers", image: imgJassen },
  { label: "Werkkleding", key: "workwear", description: "Werkbroeken, jassen en veiligheidskleding", image: imgWerk },
  { label: "Sportkleding", key: "sport", description: "Sportshirts, broeken en teamwear", image: imgSport },
  { label: "Caps & Mutsen", key: "headwear", description: "Petten, caps en wintermutsen", image: imgCaps },
  { label: "Accessoires", key: "accessoires", description: "Sjaals, handdoeken, stropdassen en meer", image: imgAccessoires },
  { label: "Tassen", key: "bag", description: "Rugzakken, totes en sporttassen", image: imgTassen },
  { label: "Kids & Baby", key: "kids", description: "Kleding voor de allerkleinsten", image: imgKids },
];


function getPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

const Producten = () => {
  const [params, setParams] = useSearchParams();
  const rawCategory = params.get("cat") ?? "";
  const category = rawCategory === "all" ? "" : rawCategory;
  const showProducts = category !== "" || (params.get("q") ?? "") !== "";

  const [page, setPage] = useState(1);
  const brandParam = params.get("brand") ?? "";
  const selectedBrands = brandParam ? brandParam.split(",").filter(Boolean) : [];
  const searchParam = params.get("q") ?? "";
  const sortParam = (params.get("sort") as "name" | "brand" | "newest") || "name";
  const [searchInput, setSearchInput] = useState(searchParam);
  const pageSizeParam = parseInt(params.get("ps") ?? "", 10);
  const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSizeParam)
    ? pageSizeParam
    : DEFAULT_PAGE_SIZE;

  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  useEffect(() => {
    document.title = "Producten — Magic Print";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Bekijk ons volledige assortiment van duizenden producten. T-shirts, polo's, hoodies, werkkleding en meer — allemaal te bedrukken of borduren met uw logo.",
      );
  }, []);

  // Reset page when category, brand, search or sort changes
  useEffect(() => {
    setPage(1);
  }, [category, brandParam, searchParam, sortParam]);

  const { data, isLoading, isFetching, isError, error } = useToptexProducts({
    page,
    pageSize: pageSize,
    category,
    brand: brandParam,
    search: searchParam,
    sort: sortParam,
    enabled: showProducts,
  });

  const items: NormProduct[] = data?.items ?? [];
  const total = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const applySearch = (value: string) => {
    const next = new URLSearchParams(params);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    next.delete("brand");
    setParams(next, { replace: true });
  };

  const setSort = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === "name") next.delete("sort");
    else next.set("sort", value);
    setParams(next, { replace: true });
  };

  const setCategory = (key: string) => {
    const next = new URLSearchParams(params);
    if (key) next.set("cat", key); else next.delete("cat");
    next.delete("brand"); // reset brand filter when category changes
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleBrand = (brandId: string) => {
    const set = new Set(selectedBrands);
    if (set.has(brandId)) set.delete(brandId); else set.add(brandId);
    const next = new URLSearchParams(params);
    if (set.size === 0) next.delete("brand");
    else next.set("brand", Array.from(set).join(","));
    setParams(next, { replace: true });
  };

  const clearBrands = () => {
    const next = new URLSearchParams(params);
    next.delete("brand");
    setParams(next, { replace: true });
  };

  const setPageSize = (n: number) => {
    const next = new URLSearchParams(params);
    if (n === DEFAULT_PAGE_SIZE) next.delete("ps");
    else next.set("ps", String(n));
    setParams(next, { replace: true });
    setPage(1);
  };

  const availableBrands = data?.brands ?? [];

  const currentCat = CATEGORIES.find((c) => c.key === category);

  const SITE_URL = "https://www.magicprint.nl";
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname}`;
  const ogImage = currentCat ? `${SITE_URL}${currentCat.image}` : `${SITE_URL}/og-image.jpg`;
  const pageTitle = currentCat ? `${currentCat.label} bedrukken — Magic Print` : "Producten — Magic Print";
  const pageDesc = currentCat
    ? `${currentCat.label} bedrukken of borduren met uw eigen logo bij Magic Print Den Haag. ${currentCat.description}.`
    : "Bekijk ons volledige assortiment van duizenden producten. T-shirts, polo's, hoodies, werkkleding en meer — allemaal te bedrukken of borduren met uw logo.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Magic Print" />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <Navbar />

      <main className="pt-32 md:pt-40 pb-20 min-h-screen bg-background">
        <div className="container">
          {!showProducts ? (
            <>
              <div className="max-w-3xl mb-10">
                <h1 className="text-3xl md:text-5xl font-bold mb-4 text-[#0a2933]">Ons assortiment</h1>
                <p className="text-base text-muted-foreground">
                  Kies een categorie om snel het juiste product te vinden. Alle producten kunnen we voor u bedrukken of borduren.
                </p>
              </div>

              <form
                className="flex items-center gap-2 max-w-xl mb-10"
                onSubmit={(e) => {
                  e.preventDefault();
                  applySearch(searchInput);
                }}
              >
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Zoek op naam, merk of referentie…"
                  aria-label="Zoek in producten"
                  className="flex-1 h-11 px-3 rounded-md border border-border bg-white text-sm"
                />
                <Button type="submit" className="h-11">Zoeken</Button>
              </form>


              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 mb-16">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className="group relative text-left rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all aspect-[3/4]"
                  >
                    <img
                      src={c.image}
                      alt={c.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a2933]/85 via-[#0a2933]/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                      <h2 className="text-2xl font-bold mb-1">{c.label}</h2>
                      <p className="text-sm text-white/85 mb-3">{c.description}</p>
                      <span className="inline-flex items-center text-sm font-semibold">
                        Bekijk producten <ArrowRight className="ml-1.5 w-4 h-4" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="max-w-2xl mx-auto text-center p-8 rounded-2xl bg-card border border-border">
                <h2 className="text-2xl font-bold mb-3 text-[#0a2933]">Iets speciaals nodig?</h2>
                <p className="text-muted-foreground mb-6">
                  Vindt u niet precies wat u zoekt? Neem contact met ons op en wij helpen u graag met
                  een passend advies en een offerte op maat.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button size="lg" asChild>
                    <Link to="/offerte-aanvragen">
                      Gratis offerte aanvragen <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/contact">Contact opnemen</Link>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => setCategory("")}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"
              >
                <ArrowLeft className="w-4 h-4" /> Terug naar categorieën
              </button>

              <div className="max-w-3xl mb-6">
                <h1 className="text-3xl md:text-5xl font-bold mb-4 text-[#0a2933]">
                  {currentCat?.label ?? "Producten"}
                </h1>
                <p className="text-base text-muted-foreground mb-5">
                  {total > 0 ? (
                    <>
                      <strong>{total.toLocaleString("nl-NL")}</strong>{" "}
                      {currentCat?.label.toLowerCase()}
                      {" "}om te bedrukken of borduren. Vind uw favoriet en vraag direct een offerte aan.
                    </>
                  ) : (
                    "Duizenden producten om te bedrukken of borduren."
                  )}
                </p>
              </div>

              {/* Category chips */}
              <div className="flex flex-wrap gap-2 mb-6">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition ${
                      category === c.key
                        ? "bg-primary text-primary-foreground"
                        : "bg-card border border-border text-foreground hover:border-primary"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* Brand filter */}
              <div className="mb-6 p-4 rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-[#0a2933]">
                    Filter op merk
                    {selectedBrands.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        ({selectedBrands.length} actief)
                      </span>
                    )}
                  </div>
                  {selectedBrands.length > 0 && (
                    <button
                      onClick={clearBrands}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      Wis filter
                    </button>
                  )}
                </div>
                {availableBrands.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    {isLoading || isFetching ? "Merken laden…" : "Geen merken beschikbaar voor deze categorie."}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableBrands.slice(0, 24).map((b) => {
                      const active = selectedBrands.includes(b.id);
                      return (
                        <button
                          key={b.id}
                          onClick={() => toggleBrand(b.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-white border-border text-foreground hover:border-primary"
                          }`}
                        >
                          {b.name}
                          <span className={`ml-1.5 ${active ? "opacity-80" : "text-muted-foreground"}`}>
                            ({b.count})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Toolbar: zoeken, sorteren, paginagrootte */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <form
                  className="flex items-center gap-2 flex-1 min-w-[240px]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    applySearch(searchInput);
                  }}
                >
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Zoek op naam, merk of referentie…"
                    aria-label="Zoek in producten"
                    className="flex-1 h-10 px-3 rounded-md border border-border bg-white text-sm"
                  />
                  <Button type="submit" variant="outline" className="h-10">Zoeken</Button>
                  {searchParam && (
                    <button
                      type="button"
                      onClick={() => applySearch("")}
                      className="text-xs text-primary hover:underline"
                    >
                      Wissen
                    </button>
                  )}
                </form>
                <div className="flex items-center gap-2 text-xs">
                  <label htmlFor="sort" className="text-muted-foreground">Sorteren:</label>
                  <select
                    id="sort"
                    value={sortParam}
                    onChange={(e) => setSort(e.target.value)}
                    className="h-10 px-2 rounded-md border border-border bg-white text-sm"
                  >
                    <option value="name">Naam (A–Z)</option>
                    <option value="brand">Merk</option>
                    <option value="newest">Nieuwste eerst</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="text-xs text-muted-foreground">
                  {total > 0 && (
                    <>
                      Toont <strong className="text-[#0a2933]">{Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)}</strong> van {total.toLocaleString("nl-NL")}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Per pagina:</span>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <button
                      key={n}
                      onClick={() => setPageSize(n)}
                      className={`px-2.5 py-1 rounded-md border font-medium transition ${
                        pageSize === n
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-white border-border text-foreground hover:border-primary"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>


              {isError && (
                <div className="p-6 rounded-xl border border-destructive/40 bg-destructive/5 text-destructive mb-8">
                  Producten konden niet geladen worden. {(error as Error)?.message}
                </div>
              )}


              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5 mb-10">
                {isLoading
                  ? Array.from({ length: pageSize }).map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-border bg-card overflow-hidden animate-pulse"
                      >
                        <div className="aspect-square bg-muted" />
                        <div className="p-3 space-y-2">
                          <div className="h-3 bg-muted rounded w-2/3" />
                          <div className="h-4 bg-muted rounded w-full" />
                        </div>
                      </div>
                    ))
                  : items.map((p) => <ProductCard key={p.ref} product={p} />)}
              </div>

              {!isLoading && items.length === 0 && !isError && (
                <p className="text-center text-muted-foreground py-12">
                  Geen producten gevonden. Probeer een andere zoekterm of categorie.
                </p>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-16">
                  <Button
                    variant="outline"
                    disabled={page <= 1 || isFetching}
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    <ChevronLeft className="mr-1.5 w-3.5 h-3.5" /> Vorige
                  </Button>
                  {getPageNumbers(page, totalPages).map((n, i) =>
                    n === "..." ? (
                      <span key={`e-${i}`} className="px-2 text-sm text-muted-foreground">…</span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => {
                          setPage(n as number);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        disabled={isFetching}
                        className={`min-w-[2.5rem] h-10 px-3 rounded-md text-sm font-medium transition ${
                          n === page
                            ? "bg-primary text-primary-foreground"
                            : "border border-border bg-card text-foreground hover:border-primary"
                        }`}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    disabled={page >= totalPages || isFetching}
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Volgende <ChevronRight className="ml-1.5 w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

function ProductPlaceholder() {
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12 text-muted-foreground/40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10 L26 6 L38 6 L44 10 L56 18 L48 26 L44 22 L44 56 L20 56 L20 22 L16 26 L8 18 Z" />
    </svg>
  );
}

function ProductCard({ product: p }: { product: NormProduct }) {
  const colorCount = p.colors?.length || 0;
  return (
    <Link
      to={`/producten/detail/${encodeURIComponent(p.ref)}`}
      className="group rounded-xl border border-border bg-white overflow-hidden hover:border-primary transition-all hover:shadow-md flex flex-col"
    >
      <div className="block aspect-square bg-white flex items-center justify-center overflow-hidden">
        {p.imageBase ? (
          <img
            src={imageSrc(p.imageBase, 300)}
            srcSet={imageSrcSet(p.imageBase)}
            sizes="(max-width: 768px) 45vw, 300px"
            onError={imageFallback}
            alt={`${p.brand} ${p.name}`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform"
          />
        ) : (
          <ProductPlaceholder />
        )}
      </div>

      <div className="p-3 flex flex-col flex-1">
        <div className="text-[11px] text-muted-foreground mb-0.5">{p.brand}</div>
        <h3 className="font-semibold text-sm leading-tight mb-0.5 line-clamp-2 text-[#0a2933] group-hover:text-primary transition-colors">
          {p.name}
        </h3>
        {p.type && <div className="text-xs text-muted-foreground mb-2">{p.type}</div>}
        <div className="mt-auto flex items-center justify-between pt-1">
          <div className="flex -space-x-1">
            {p.colors?.slice(0, 5).map((c, i) => (
              <span
                key={i}
                title={c.name}
                className="w-4 h-4 rounded-full border border-border"
                style={{ backgroundColor: c.hex ?? undefined }}
              />
            ))}
            {colorCount > 5 && (
              <span className="text-[10px] text-muted-foreground ml-2 self-center">
                +{colorCount - 5}
              </span>
            )}
          </div>
          <span className="text-[11px] text-primary font-medium">{p.ref}</span>
        </div>
      </div>
    </Link>
  );
}

export default Producten;
